/**
 * The Nova loader's geometry and choreography, shared by web and native.
 *
 * Nova is the flat ink silhouette from Nova Studio's master vector, with two
 * transparent eyes. The body breathes; the eyes carry the status:
 *
 *   thinking   2.4s  — look upward, settle, blink, rest
 *   writing    1.4s  — an alternating rhythm and an attentive downward gaze;
 *                      the right eye runs 1.24s out of phase with the left
 *   rewriting  1.9s  — follow a line, return, then blink
 *   listening  3.6s  — an attentive, near-still face; while dictation is
 *                      running it is the mic level, not the clock, that
 *                      carries the expression (see `NOVA_LISTEN`)
 *
 * Web drives these as CSS keyframes (`.nova-loader__eye` in global.css);
 * native samples the same keyframes on the UI thread in `nova-loader.tsx`.
 * Both read from here so a change to one platform's motion cannot drift from
 * the other's. Every value is in the SVG's own user units (viewBox 500×500).
 */
import type { TracePhase } from './activity-trace-model';

export type NovaStatus = 'thinking' | 'writing' | 'rewriting' | 'listening';

/** Nova Studio Nova-master.svg, Default silhouette. */
export const NOVA_BODY_PATH =
  'M510.205 254.828C553.542 254.828 550.574 294.46 637.97 371.173C674.493 403.231 698.726 389.844 717.199 417.568C730.295 437.223 712.686 460.411 704.357 517.979C704.355 518.056 704.355 518.133 704.353 518.21C701.498 632.924 628.85 720.208 510.206 720.208C391.562 720.208 318.915 632.924 316.06 518.21C316.058 518.137 316.058 518.064 316.056 517.991C307.728 460.415 290.114 437.224 303.211 417.568C321.684 389.844 345.918 403.231 382.44 371.173C469.836 294.46 466.868 254.828 510.205 254.828Z';

export const NOVA_VIEWBOX = { x: 260, y: 240, width: 500, height: 500 } as const;

export const NOVA_EYES = {
  rx: 32.8244,
  ry: 63.3042,
  left: { cx: 430.738, cy: 539.437 },
  right: { cx: 590.17, cy: 539.437 },
} as const;

/** One full cycle of the eye animation per status. */
export const NOVA_CYCLE_MS: Record<NovaStatus, number> = {
  thinking: 2400,
  writing: 1400,
  rewriting: 1900,
  listening: 3600,
};

/** The label's shimmer sweep, shared with every other running status label. */
export const NOVA_SHIMMER_MS = 1400;

/**
 * The right eye's phase offset while writing: CSS `animation-delay: -1.24s`
 * on a 1.4s cycle, i.e. it starts 1.24s into its loop.
 */
export const NOVA_WRITE_RIGHT_EYE_OFFSET = 1240 / NOVA_CYCLE_MS.writing;

/** Body opacity: dim at the ends of the cycle, full at 45%. */
export const NOVA_BREATHE = { min: 0.65, max: 1, peakAt: 0.45 } as const;

/**
 * An eye pose at a point in the cycle: translation from its resting centre and
 * a vertical scale about that centre (a blink is `sy` ≈ 0.13).
 */
export type EyeKeyframe = { at: number; tx: number; ty: number; sy: number };

const rest = (at: number): EyeKeyframe => ({ at, tx: 0, ty: 0, sy: 1 });

export const NOVA_EYE_KEYFRAMES: Record<NovaStatus, readonly EyeKeyframe[]> = {
  // 0%, 12%, 53%, 65%, 80%, 100% rest · 28%, 38% up-right · 72% blink
  thinking: [
    rest(0),
    rest(0.12),
    { at: 0.28, tx: 12, ty: -22, sy: 0.94 },
    { at: 0.38, tx: 12, ty: -22, sy: 0.94 },
    rest(0.53),
    rest(0.65),
    { at: 0.72, tx: 0, ty: 0, sy: 0.13 },
    rest(0.8),
    rest(1),
  ],
  // 0%, 12%, 68%, 100% down · 32% up and narrowed · 49% slightly down, open
  writing: [
    { at: 0, tx: 0, ty: 7, sy: 0.94 },
    { at: 0.12, tx: 0, ty: 7, sy: 0.94 },
    { at: 0.32, tx: 0, ty: -10, sy: 0.72 },
    { at: 0.49, tx: 0, ty: 5, sy: 1 },
    { at: 0.68, tx: 0, ty: 7, sy: 0.94 },
    { at: 1, tx: 0, ty: 7, sy: 0.94 },
  ],
  // 0%, 52%, 72%, 86%, 100% rest · 26% a small attentive lift · 79% blink.
  // Deliberately sparse: amplitude does the work, the clock only blinks.
  listening: [
    rest(0),
    { at: 0.26, tx: 0, ty: -4, sy: 1.06 },
    rest(0.52),
    rest(0.72),
    { at: 0.79, tx: 0, ty: 0, sy: 0.13 },
    rest(0.86),
    rest(1),
  ],
  // 0%, 100% rest · 18% left · 50% right · 68%, 88% rest · 78% blink
  rewriting: [
    rest(0),
    { at: 0.18, tx: -23, ty: 5, sy: 0.88 },
    { at: 0.5, tx: 23, ty: 5, sy: 0.88 },
    rest(0.68),
    { at: 0.78, tx: 0, ty: 0, sy: 0.13 },
    rest(0.88),
    rest(1),
  ],
};

/**
 * CSS `cubic-bezier(.4, 0, .2, 1)` — the "standard" material curve the eye
 * keyframes ease between. Solved numerically so it can run on the UI thread.
 */
export function novaEyeEase(t: number): number {
  'worklet';
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  const x1 = 0.4;
  const y1 = 0;
  const x2 = 0.2;
  const y2 = 1;
  // Newton–Raphson on the x(t) polynomial, then evaluate y at that t.
  let u = t;
  for (let i = 0; i < 6; i += 1) {
    const mt = 1 - u;
    const x = 3 * mt * mt * u * x1 + 3 * mt * u * u * x2 + u * u * u;
    const dx = 3 * mt * mt * x1 + 6 * mt * u * (x2 - x1) + 3 * u * u * (1 - x2);
    if (dx === 0) break;
    u -= (x - t) / dx;
    if (u < 0) u = 0;
    if (u > 1) u = 1;
  }
  const mt = 1 - u;
  return 3 * mt * mt * u * y1 + 3 * mt * u * u * y2 + u * u * u;
}

/** Pose of an eye at cycle position `t` ∈ [0, 1), easing between keyframes. */
export function sampleEyePose(
  frames: readonly EyeKeyframe[],
  t: number,
): { tx: number; ty: number; sy: number } {
  'worklet';
  const clamped = t - Math.floor(t);
  for (let i = 1; i < frames.length; i += 1) {
    const from = frames[i - 1]!;
    const to = frames[i]!;
    if (clamped <= to.at) {
      const span = to.at - from.at;
      const local = span <= 0 ? 1 : novaEyeEase((clamped - from.at) / span);
      return {
        tx: from.tx + (to.tx - from.tx) * local,
        ty: from.ty + (to.ty - from.ty) * local,
        sy: from.sy + (to.sy - from.sy) * local,
      };
    }
  }
  const last = frames[frames.length - 1]!;
  return { tx: last.tx, ty: last.ty, sy: last.sy };
}

/**
 * How far the mic level bends the resting face while listening.
 *
 * The eyes widen and lift as the voice gets louder and the body squashes a
 * fraction — the shape of leaning in. Everything here multiplies or offsets
 * the pose the keyframes already produced, so the blink and the drift keep
 * running underneath. `eyeBase` sits below 1 on purpose: silence should read
 * as a slightly narrowed, resting face rather than a startled one.
 */
export const NOVA_LISTEN = {
  eyeBase: 0.82,
  eyeGain: 0.42,
  /** User units the eyes rise by at full level. */
  eyeLift: 15,
  /** Scale traded between the body's axes at full level. */
  squash: 0.055,
} as const;

/** Eye widening and lift for a 0..1 mic level. */
export function sampleListenEye(level: number): { sy: number; ty: number } {
  'worklet';
  const l = level < 0 ? 0 : level > 1 ? 1 : level;
  return { sy: NOVA_LISTEN.eyeBase + NOVA_LISTEN.eyeGain * l, ty: -NOVA_LISTEN.eyeLift * l };
}

/** Body squash for a 0..1 mic level. Area-preserving, so it reads as a breath. */
export function sampleListenBody(level: number): { sx: number; sy: number } {
  'worklet';
  const l = level < 0 ? 0 : level > 1 ? 1 : level;
  return { sx: 1 + NOVA_LISTEN.squash * l, sy: 1 - NOVA_LISTEN.squash * l };
}

/** Body opacity at cycle position `t`: CSS `ease-in-out` between the ends and the 45% peak. */
export function sampleBreathe(t: number): number {
  'worklet';
  const clamped = t - Math.floor(t);
  const { min, max, peakAt } = NOVA_BREATHE;
  const local = clamped <= peakAt ? clamped / peakAt : 1 - (clamped - peakAt) / (1 - peakAt);
  // ease-in-out ≈ cubic-bezier(.42, 0, .58, 1); a smoothstep is visually identical here.
  const eased = local * local * (3 - 2 * local);
  return min + (max - min) * eased;
}

/**
 * Which face the loader wears for a turn in a given phase.
 *
 * `thinking` is the wait for the first thing to arrive; `writing` is the wait
 * for prose once the tools are done; a turn that is running as a regeneration
 * (a second branch of the same message) is `rewriting`.
 */
export function resolveNovaStatus(params: { phase: TracePhase; regenerating: boolean }): NovaStatus {
  if (params.regenerating) return 'rewriting';
  return params.phase === 'writing' ? 'writing' : 'thinking';
}

export const NOVA_LABEL_KEYS: Record<NovaStatus, string> = {
  thinking: 'chat.activity.phase.thinking',
  writing: 'chat.activity.phase.writing',
  rewriting: 'chat.activity.phase.rewriting',
  listening: 'voice.ptt.recording',
};
